import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Empty, Pagination, Select, Spin } from 'antd';
import type { TableProps } from 'antd';

/**
 * Phone / small-tablet view of a table (2026-10-19, mockup QgufCDP5cwvvQ7AbSStaop, decision M2-a):
 * each row becomes a card built from the table's OWN columns, so every cell renderer (links, prices,
 * buttons, inputs, tick boxes) keeps working without a second implementation.
 *
 *   header  – the columns named in `layout.header` (default: the first column with a title), stacked
 *   body    – every other titled column as a "label · value" row (empty values skipped)
 *   footer  – the columns named in `layout.footer` plus untitled wide columns (action buttons), full width
 *
 * Untitled narrow columns (≤ 60 px, e.g. a tick box) sit at the card's top left.
 */
export interface CardLayout {
  /** Column keys shown at the card's top left (tick boxes). Untitled narrow columns go there too. */
  pickers?: string[];
  /** Column keys shown in the card header, in this order. */
  header?: string[];
  /** Column keys shown full width at the bottom (actions). */
  footer?: string[];
  /** Column keys never shown on a card. */
  hidden?: string[];
  /** When set, only these body keys show; the rest sit behind "More details". */
  body?: string[];
  /** A totals line under the cards (what the table shows in its summary row, which cards cannot render). */
  total?: React.ReactNode;
}

type AnyCol = any;

export const columnKeyOf = (c: AnyCol, i: number): string =>
  c.key != null ? String(c.key)
    : Array.isArray(c.dataIndex) ? c.dataIndex.join('.')
    : c.dataIndex != null ? String(c.dataIndex)
    : `col-${i}`;

const flatten = (cols: AnyCol[]): AnyCol[] => cols.flatMap((c) => (c && c.children ? flatten(c.children) : [c])).filter(Boolean);

const valueOf = (record: any, dataIndex: any) =>
  dataIndex == null ? undefined
    : Array.isArray(dataIndex) ? dataIndex.reduce((o: any, k: any) => (o == null ? o : o[k]), record)
    : record?.[dataIndex];

function renderCell(c: AnyCol, record: any, index: number): React.ReactNode {
  const v = valueOf(record, c.dataIndex);
  const out = c.render ? c.render(v, record, index) : v;
  // antd lets a renderer return { children, props } (colSpan etc.)
  if (out && typeof out === 'object' && !React.isValidElement(out) && !Array.isArray(out) && 'children' in out) return (out as any).children;
  if (typeof out === 'number') return String(out);
  return out as React.ReactNode;
}

const titleOf = (c: AnyCol): React.ReactNode => (typeof c.title === 'function' ? c.title({}) : c.title);
const dash = (t: unknown) => t === '-' || t === '—';
// Empty values are skipped on a card, including a renderer's grey "-" (an element whose only text is a dash).
const blank = (n: React.ReactNode): boolean =>
  n === null || n === undefined || n === false || n === '' || dash(n) ||
  (React.isValidElement(n) && dash((n.props as any)?.children));
const narrow = (c: AnyCol) => typeof c.width === 'number' && c.width <= 60;
const isActions = (c: AnyCol) => typeof c.title === 'string' && /^actions?$/i.test(c.title.trim());

export interface CardListProps<T> extends Omit<TableProps<T>, 'columns'> {
  columns: AnyCol[];
  layout?: CardLayout;
}

function CardList<T extends object = any>(props: CardListProps<T>) {
  const { columns, dataSource, rowKey, loading, pagination, rowSelection, onRow, expandable, layout, locale, onChange } = props;
  const rows = (dataSource || []) as T[];
  const cols = useMemo(() => flatten(columns as AnyCol[]).map((c, i) => ({ c, key: columnKeyOf(c, i) })), [columns]);
  const hidden = new Set(layout?.hidden || []);
  const visible = cols.filter(({ key }) => !hidden.has(key));
  const headerKeys = layout?.header?.length ? layout.header : [visible.find(({ c }) => !blank(titleOf(c)))?.key].filter(Boolean) as string[];
  const footerKeys = new Set(layout?.footer || []);
  const pickerKeys = new Set(layout?.pickers || []);
  const pickers = visible.filter(({ c, key }) => pickerKeys.has(key) || (blank(titleOf(c)) && narrow(c) && !headerKeys.includes(key) && !footerKeys.has(key)));
  const headers = headerKeys.map((k) => visible.find((v) => v.key === k)).filter(Boolean) as { c: AnyCol; key: string }[];
  const footers = visible.filter(({ c, key }) => footerKeys.has(key) || ((blank(titleOf(c)) || isActions(c)) && !narrow(c) && !headerKeys.includes(key) && !pickerKeys.has(key)));
  const bodyAll = visible.filter(({ key }) => !headerKeys.includes(key) && !footers.some((f) => f.key === key) && !pickers.some((p) => p.key === key));
  const bodyMain = layout?.body ? bodyAll.filter(({ key }) => layout.body!.includes(key)) : bodyAll;
  const bodyMore = layout?.body ? bodyAll.filter(({ key }) => !layout.body!.includes(key)) : [];

  // Sorting: cards have no column headers to click, so sortable columns become a "Sort by" picker.
  const sortable = visible.filter(({ c }) => c.sorter);
  const initialSort = sortable.find(({ c }) => c.sortOrder || c.defaultSortOrder);
  const [sortBy, setSortBy] = useState<string | undefined>(initialSort ? `${initialSort.key}:${initialSort.c.sortOrder || initialSort.c.defaultSortOrder}` : undefined);
  const sortCol = sortBy ? sortable.find(({ key }) => key === sortBy.split(':')[0]) : undefined;
  const compare = sortCol && (typeof sortCol.c.sorter === 'function' ? sortCol.c.sorter : sortCol.c.sorter?.compare);
  const sortedRows = useMemo(() => {
    if (!sortBy || typeof compare !== 'function') return rows;
    const dir = sortBy.endsWith(':descend') ? -1 : 1;
    return [...rows].sort((a, b) => dir * compare(a, b));
  }, [rows, sortBy, compare]);
  const changeSort = (v?: string) => {
    setSortBy(v);
    const col = v ? sortable.find(({ key }) => key === v.split(':')[0]) : undefined;
    // Server-sorted tables (sorter: true) hear about it the way antd tells them.
    if (onChange && (!col || col.c.sorter === true)) {
      (onChange as any)(pagination || {}, {}, col ? { column: col.c, columnKey: col.key, field: col.c.dataIndex, order: v!.split(':')[1] } : {}, { action: 'sort' });
    }
  };

  const keyFor = (r: T, i: number): React.Key =>
    typeof rowKey === 'function' ? (rowKey as any)(r, i) : rowKey ? (r as any)[rowKey as string] : (r as any).key ?? i;

  // Pagination: server-side when the table says so (total beyond what it holds), else on the rows given.
  const pg: any = pagination === false ? null : (pagination || {});
  const serverSide = !!pg && pg.total != null && pg.total > rows.length;
  const [localPage, setLocalPage] = useState(1);
  const [localSize, setLocalSize] = useState<number>(pg?.pageSize || pg?.defaultPageSize || 10);
  const page = pg?.current ?? localPage;
  const size = pg?.pageSize ?? localSize;
  const shown = !pg || serverSide ? sortedRows : sortedRows.slice((page - 1) * size, page * size);
  const total = serverSide ? pg.total : rows.length;
  const changePage = (p: number, s: number) => {
    setLocalPage(p); setLocalSize(s);
    if (pg?.onChange) pg.onChange(p, s);
    else if (onChange) (onChange as any)({ ...(pg || {}), current: p, pageSize: s }, {}, {}, { action: 'paginate' });
  };

  // Expansion: controlled when the table passes expandedRowKeys, else local.
  const [openLocal, setOpenLocal] = useState<React.Key[]>([]);
  const exp: any = expandable;
  const openKeys: React.Key[] = exp?.expandedRowKeys ?? openLocal;
  const toggleOpen = (k: React.Key, r: T) => {
    const isOpen = openKeys.includes(k);
    const next = isOpen ? openKeys.filter((x) => x !== k) : [...openKeys, k];
    if (exp?.expandedRowKeys) { exp.onExpandedRowsChange?.(next); exp.onExpand?.(!isOpen, r); } else setOpenLocal(next);
  };
  const [moreOpen, setMoreOpen] = useState<React.Key[]>([]);

  const sel: any = rowSelection;
  const selectedKeys: React.Key[] = sel?.selectedRowKeys || [];
  const toggleSel = (k: React.Key, r: T, checked: boolean) => {
    const next = checked ? [...selectedKeys, k] : selectedKeys.filter((x) => x !== k);
    const nextRows = rows.filter((row, i) => next.includes(keyFor(row, i)));
    sel?.onChange?.(next, nextRows, { type: 'single' });
  };

  if (loading && !rows.length) return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;
  if (!rows.length) return <div style={{ padding: 16 }}>{(locale as any)?.emptyText ?? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}</div>;

  return (
    <Spin spinning={!!loading}>
      {sortable.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13, color: '#5f6672' }}>
          <span>Sort by</span>
          <Select
            size="middle"
            allowClear
            placeholder="as listed"
            style={{ flex: '1 1 auto', maxWidth: 260 }}
            value={sortBy}
            onChange={changeSort}
            options={sortable.flatMap(({ c, key }) => {
              const t = titleOf(c);
              const label = typeof t === 'string' ? t : key;
              return [{ value: `${key}:descend`, label: `${label} ↓` }, { value: `${key}:ascend`, label: `${label} ↑` }];
            })}
          />
        </div>
      )}
      <div className="lasheen-card-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))', gap: 10, alignItems: 'start' }}>
        {shown.map((r, idx) => {
          const index = serverSide || !pg ? idx : (page - 1) * size + idx;
          const k = keyFor(r, index);
          const rowProps: any = onRow ? onRow(r, index) : {};
          const canExpand = exp?.expandedRowRender && (!exp.rowExpandable || exp.rowExpandable(r));
          const cbProps = sel?.getCheckboxProps ? sel.getCheckboxProps(r) : {};
          const more = moreOpen.includes(k);
          const field = ({ c, key }: { c: AnyCol; key: string }) => {
            const content = renderCell(c, r, index);
            if (blank(content)) return null;
            return (
              <React.Fragment key={key}>
                <div style={{ color: '#5f6672', fontSize: 12, lineHeight: '20px' }}>{titleOf(c)}</div>
                <div style={{ textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}>{content}</div>
              </React.Fragment>
            );
          };
          return (
            <div
              key={String(k)}
              className={`lasheen-card ${rowProps.className || ''}`}
              onClick={rowProps.onClick}
              style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, cursor: rowProps.onClick ? 'pointer' : undefined, ...(rowProps.style || {}) }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                {sel && (
                  <span onClick={(e) => e.stopPropagation()} style={{ minWidth: 28, minHeight: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Checkbox checked={selectedKeys.includes(k)} disabled={cbProps.disabled} onChange={(e) => toggleSel(k, r, e.target.checked)} aria-label="Select" />
                  </span>
                )}
                {pickers.map(({ c, key }) => (
                  <span key={key} onClick={(e) => e.stopPropagation()} style={{ minWidth: 28 }}>{renderCell(c, r, index)}</span>
                ))}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {headers.map(({ c, key }) => {
                    const content = renderCell(c, r, index);
                    return blank(content) ? null : <div key={key} style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{content}</div>;
                  })}
                </div>
              </div>
              {bodyMain.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: '4px 12px', fontSize: 13, alignItems: 'start' }}>
                  {bodyMain.map(field)}
                  {more && bodyMore.map(field)}
                </div>
              )}
              {bodyMore.length > 0 && (
                <Button type="link" size="small" style={{ padding: 0, alignSelf: 'flex-start' }} onClick={(e) => { e.stopPropagation(); setMoreOpen((m) => (m.includes(k) ? m.filter((x) => x !== k) : [...m, k])); }}>
                  {more ? 'Fewer details' : 'More details'}
                </Button>
              )}
              {footers.length > 0 && (
                <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, borderTop: '1px solid #f5f5f5', paddingTop: 8 }}>
                  {footers.map(({ c, key }) => {
                    const content = renderCell(c, r, index);
                    return blank(content) ? null : <div key={key} style={{ minWidth: 0, maxWidth: '100%' }}>{content}</div>;
                  })}
                </div>
              )}
              {canExpand && (
                <div onClick={(e) => e.stopPropagation()}>
                  <Button size="small" onClick={() => toggleOpen(k, r)}>{openKeys.includes(k) ? 'Hide lines' : 'Show lines'}</Button>
                  {openKeys.includes(k) && <div style={{ marginTop: 8, overflowX: 'auto' }}>{exp.expandedRowRender(r, index, 0, true)}</div>}
                </div>
              )}
            </div>
          );
        })}
        {pg && total > size && (
          <Pagination
            style={{ gridColumn: '1 / -1', justifySelf: 'center', marginTop: 4 }}
            current={page}
            pageSize={size}
            total={total}
            size="small"
            showSizeChanger={false}
            onChange={changePage}
          />
        )}
      </div>
      {layout?.total && (
        <div style={{ marginTop: 10, padding: '8px 12px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 10, fontSize: 13 }}>{layout.total}</div>
      )}
    </Spin>
  );
}

export default CardList;
